import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { apiErrorMessage } from '../../pages/admin/transactions/utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * The Investec account a bank row came from tags it — when it has no investor
 * yet. A row already tagged to an RP code keeps it; move it by reallocating by
 * hand (which stamps the new RP code). Preview → apply
 * (backend: services/recon_cleanup.apply_account_mapping_tags).
 */
export function ApplyAccountMappingsCard({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const check = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/investec/apply-account-mappings`, { headers });
      setPreview(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Check failed'));
    } finally { setBusy(false); }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/investec/apply-account-mappings`, {}, { headers });
      toast.success(`Tagged ${r.data.tagged}, classified ${r.data.classified} bank transaction(s)`);
      setPreview(null);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Apply failed'));
    } finally { setBusy(false); }
  };

  const total = preview ? preview.tagged + (preview.classified || 0) : 0;

  return (
    <Card data-testid="apply-account-mappings-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2"><Link2 className="h-5 w-5" /> Apply account mappings</CardTitle>
        <CardDescription>
          Transactions pulled from an investor's own Investec account with <em>no investor yet</em> are tagged to that
          investor. A transaction already tagged to an RP code always stays with that RP code — to move it, reallocate it
          by hand and it takes the new investor's RP code. Unmapped accounts are left for you to allocate.
          Unclassified transactions get the bank statement's direction: money in = credit, money out = debit.
          Runs automatically on every sync and when you save a mapping.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button variant="outline" size="sm" onClick={check} disabled={busy} data-testid="apply-mappings-check">
          {busy && !preview ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Check
        </Button>
        {preview && (
          <div className="space-y-3 text-sm">
            {total === 0 ? (
              <p className="text-green-700">Every transaction from a mapped investor account already has an investor and a type.</p>
            ) : (
              <>
                {preview.classified > 0 && (
                  <p>{preview.classified} unclassified bank transaction(s) will be set to <strong>credit</strong> (money in) or <strong>debit</strong> (money out), as on the bank statement.</p>
                )}
                {preview.per_account.map((a, i) => (
                  <div key={i} className="flex justify-between border-b pb-1">
                    <span>{a.account} → <strong>{a.investor}</strong></span>
                    <span className="text-slate-600">{a.tagged} untagged</span>
                  </div>
                ))}
                <Button size="sm" onClick={apply} disabled={busy} data-testid="apply-mappings-run">
                  {busy ? 'Applying…' : `Apply to ${total} transaction(s)`}
                </Button>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ApplyAccountMappingsCard;
